/**
 * Settings storage: local (localStorage) and optional account-backed overlay.
 * Local keys are backward-compatible (e.g. reveal-notes-tts-lang).
 * When an account source is registered, account values override local for keys marked 'account'.
 */

import { getDefault } from './defaults';
import type { SettingsDomain } from './types';

/** Map (domain, key) → localStorage key. Keeps compatibility with existing keys. */
const LOCAL_KEYS: Record<string, string> = {
  'tts.voice': 'reveal-notes-tts-voice',
  'tts.rate': 'reveal-notes-tts-rate',
  'tts.volume': 'reveal-notes-tts-volume',
  'tts.lang': 'reveal-notes-tts-lang',
  'tts.subtitles': 'reveal-notes-tts-subtitles',
  'tts.autoAdvance': 'reveal-notes-tts-auto-advance',
  'tts.highlightColorId': 'reveal-notes-tts-highlight-color',
  'appearance.highlightColorId': 'reveal-notes-tts-highlight-color'
};

/** Lang can be stored per path. */
const LANG_PATH_PREFIX = 'reveal-notes-tts-lang-path-';

function getLocalKey(domain: SettingsDomain, key: string, pathAware?: boolean): string {
  const composite = `${domain}.${key}`;
  if (pathAware && domain === 'tts' && key === 'lang') {
    try {
      const path = typeof window !== 'undefined' && window.location?.pathname ? window.location.pathname : '';
      return LANG_PATH_PREFIX + (path.replace(/\/$/, '') || '/');
    } catch {
      return LOCAL_KEYS[composite] ?? composite;
    }
  }
  return LOCAL_KEYS[composite] ?? composite;
}

function getStorage(): Storage {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : sessionStorage;
  } catch {
    return sessionStorage;
  }
}

/** Optional: reader for account-backed settings (e.g. Firestore user prefs). When set, getSetting uses account then local then default. */
let accountReader: (() => Record<string, Record<string, unknown>> | null) | null = null;
let accountWriter: ((domain: SettingsDomain, key: string, value: unknown) => void) | null = null;

export function registerAccountSettingsReader(reader: () => Record<string, Record<string, unknown>> | null): void {
  accountReader = reader;
}

export function registerAccountSettingsWriter(
  writer: ((domain: SettingsDomain, key: string, value: unknown) => void) | null
): void {
  accountWriter = writer;
}

function readLocal(domain: SettingsDomain, key: string): string | null {
  try {
    const storageKey = getLocalKey(domain, key, true);
    return getStorage().getItem(storageKey);
  } catch {
    return null;
  }
}

/** Raw local value (for “did user set this?”). Use when getSetting would hide an explicit default. */
export function getRawLocal(domain: SettingsDomain, key: string): string | null {
  return readLocal(domain, key);
}

function writeLocal(domain: SettingsDomain, key: string, value: string | null): void {
  try {
    const storageKey = getLocalKey(domain, key, true);
    if (value != null) getStorage().setItem(storageKey, value);
    else getStorage().removeItem(storageKey);
  } catch {}
}

/** Coerce/validate by domain and key. */
function coerce(domain: SettingsDomain, key: string, raw: unknown): unknown {
  const def = getDefault(domain, key);
  if (domain === 'tts') {
    if (key === 'rate') {
      const n = typeof raw === 'string' ? parseFloat(raw) : Number(raw);
      return !isNaN(n) && n >= 0.5 && n <= 1.5 ? n : (def as number);
    }
    if (key === 'volume') {
      const n = typeof raw === 'string' ? parseFloat(raw) : Number(raw);
      return !isNaN(n) && n >= 0 && n <= 1 ? n : (def as number);
    }
    if (key === 'subtitles' || key === 'autoAdvance') return raw === 'true' || raw === true;
    if (key === 'voice') return raw == null || raw === '' ? null : String(raw);
    if (key === 'lang' || key === 'highlightColorId') return raw != null ? String(raw) : def;
  }
  if (domain === 'appearance' && key === 'highlightColorId') return raw != null ? String(raw) : def;
  return raw != null ? raw : def;
}

/**
 * Get current value: account (if reader registered) → local → default.
 */
export function getSetting(domain: SettingsDomain, key: string): unknown {
  if (accountReader) {
    const account = accountReader();
    const domainPrefs = account?.[domain];
    if (domainPrefs && key in domainPrefs) {
      return coerce(domain, key, domainPrefs[key]);
    }
  }
  const localRaw = readLocal(domain, key);
  if (localRaw !== null) return coerce(domain, key, localRaw);
  return getDefault(domain, key);
}

/**
 * Set value. Writes to local storage only. Account-backed sync (e.g. updateUserPrefs) is done by the feature/auth layer when you want to persist to account.
 */
export function setSetting(domain: SettingsDomain, key: string, value: unknown): void {
  if (value == null || value === '') {
    writeLocal(domain, key, null);
    accountWriter?.(domain, key, null);
    return;
  }
  const next = typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value);
  writeLocal(domain, key, next);
  accountWriter?.(domain, key, value);
}
